import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FiFolder,
  FiMail,
  FiTool,
  FiUser,
  FiEye,
  FiTrendingUp,
  FiPlus,
  FiActivity,
} from "react-icons/fi";
import {
  projectsAPI,
  skillsAPI,
  contactsAPI,
  profileAPI,
} from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";

const Dashboard = () => {
  const [stats, setStats] = useState({
    projects: 0,
    skills: 0,
    contacts: 0,
    newContacts: 0,
  });
  const [recentProjects, setRecentProjects] = useState([]);
  const [recentContacts, setRecentContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [projectsRes, skillsRes, contactsRes] = await Promise.all([
          projectsAPI.getProjects().catch(() => ({ data: [] })),
          skillsAPI.getSkills().catch(() => ({ data: [] })),
          contactsAPI.getContacts().catch(() => ({ data: [], stats: {} })),
        ]);

        const projects = projectsRes.data || [];
        const skills = skillsRes.data || [];
        const contacts = contactsRes.data || [];
        const contactStats = contactsRes.stats || {};

        setStats({
          projects: projects.length,
          skills: skills.length,
          contacts: contacts.length,
          newContacts:
            contactStats.new ||
            contacts.filter((c) => c.status === "new").length,
        });

        setRecentProjects(projects.slice(0, 3));
        setRecentContacts(contacts.slice(0, 5));
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading dashboard..." />
      </div>
    );
  }

  const statCards = [
    {
      title: "Total Projects",
      value: stats.projects,
      icon: FiFolder,
      color: "blue",
      link: "/projects",
      change: null,
    },
    {
      title: "Skills",
      value: stats.skills,
      icon: FiTool,
      color: "green",
      link: "/skills",
      change: null,
    },
    {
      title: "Total Messages",
      value: stats.contacts,
      icon: FiMail,
      color: "purple",
      link: "/contacts",
      change: null,
    },
    {
      title: "New Messages",
      value: stats.newContacts,
      icon: FiTrendingUp,
      color: "red",
      link: "/contacts",
      change: null,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">
            Welcome back! Here's what's happening with your portfolio.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <Link to="/projects/new" className="btn btn-primary">
            <FiPlus className="mr-2" size={16} />
            New Project
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Link
              key={index}
              to={stat.link}
              className="card hover:shadow-lg transition-shadow p-6 group"
            >
              <div className="flex items-center">
                <div
                  className={`p-3 rounded-full bg-${stat.color}-100 group-hover:bg-${stat.color}-200 transition-colors`}
                >
                  <Icon className={`text-${stat.color}-600`} size={24} />
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-gray-600">
                    {stat.title}
                  </p>
                  <div className="flex items-center">
                    <p className="text-2xl font-bold text-gray-900">
                      {stat.value}
                    </p>
                    {stat.change && (
                      <span
                        className={`ml-2 text-sm ${
                          stat.change > 0 ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {stat.change > 0 ? "+" : ""}
                        {stat.change}%
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Projects */}
        <div className="card">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <FiFolder className="mr-2 text-blue-600" />
                Recent Projects
              </h2>
              <Link
                to="/projects"
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                View all
              </Link>
            </div>
          </div>
          <div className="p-6">
            {recentProjects.length === 0 ? (
              <div className="text-center py-8">
                <FiFolder className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-500">No projects yet</p>
                <Link to="/projects/new" className="btn btn-primary mt-4">
                  <FiPlus className="mr-2" size={16} />
                  Create First Project
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {recentProjects.map((project) => (
                  <div
                    key={project._id}
                    className="flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {project.images?.[0] ? (
                      <img
                        src={project.images[0].url}
                        alt={project.title}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                        <FiFolder className="text-gray-400" size={20} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {project.title}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {new Date(project.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {project.featured && (
                        <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                          Featured
                        </span>
                      )}
                      <Link
                        to={`/projects/edit/${project._id}`}
                        className="text-blue-600 hover:text-blue-700 text-sm"
                      >
                        Edit
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Messages */}
        <div className="card">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <FiMail className="mr-2 text-purple-600" />
                Recent Messages
              </h2>
              <Link
                to="/contacts"
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                View all
              </Link>
            </div>
          </div>
          <div className="p-6">
            {recentContacts.length === 0 ? (
              <div className="text-center py-8">
                <FiMail className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-500">No messages yet</p>
                <p className="text-sm text-gray-400 mt-2">
                  Messages from your contact form will appear here
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentContacts.map((contact) => (
                  <div
                    key={contact._id}
                    className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                        <FiUser className="text-purple-600" size={16} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {contact.name}
                      </p>
                      <p className="text-sm text-gray-600 truncate">
                        {contact.subject}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(contact.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <span
                      className={`flex-shrink-0 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        contact.status === "new"
                          ? "bg-blue-100 text-blue-800"
                          : contact.status === "read"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-green-100 text-green-800"
                      }`}
                    >
                      {contact.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <FiActivity className="mr-2 text-green-600" />
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/projects/new" className="btn btn-outline w-full">
            <FiPlus className="mr-2" size={16} />
            Add Project
          </Link>
          <Link to="/skills" className="btn btn-outline w-full">
            <FiTool className="mr-2" size={16} />
            Manage Skills
          </Link>
          <Link to="/profile" className="btn btn-outline w-full">
            <FiUser className="mr-2" size={16} />
            Edit Profile
          </Link>
          <Link to="/contacts" className="btn btn-outline w-full">
            <FiMail className="mr-2" size={16} />
            View Messages
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
